'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import { saveToolScore } from '@/lib/tool-score-storage';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  category: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'security-controls',
    title: 'Security Controls',
    description: 'Technical and organizational security measures',
    questions: [
      { id: 'sc1', text: 'Does the vendor have ISO 27001 certification or equivalent?', category: 'Certification', weight: 10 },
      { id: 'sc2', text: 'Does the vendor have SOC 2 Type II report (within last 12 months)?', category: 'Certification', weight: 10 },
      { id: 'sc3', text: 'Does the vendor implement encryption for data at rest and in transit?', category: 'Data Protection', weight: 9 },
      { id: 'sc4', text: 'Does the vendor have multi-factor authentication for system access?', category: 'Access Control', weight: 9 },
      { id: 'sc5', text: 'Does the vendor perform regular vulnerability assessments and penetration testing?', category: 'Testing', weight: 9 },
      { id: 'sc6', text: 'Does the vendor have an endpoint protection/EDR solution?', category: 'Endpoint Security', weight: 8 },
      { id: 'sc7', text: 'Does the vendor have network segmentation and firewall controls?', category: 'Network Security', weight: 8 },
      { id: 'sc8', text: 'Does the vendor have a patch management program?', category: 'Vulnerability Mgmt', weight: 8 },
    ],
  },
  {
    id: 'data-handling',
    title: 'Data Handling & Privacy',
    description: 'How the vendor handles and protects your data',
    questions: [
      { id: 'dh1', text: 'Does the vendor have a documented data classification policy?', category: 'Policy', weight: 9 },
      { id: 'dh2', text: 'Can the vendor specify where your data will be stored geographically?', category: 'Data Residency', weight: 9 },
      { id: 'dh3', text: 'Does the vendor have a data retention and disposal policy?', category: 'Data Lifecycle', weight: 8 },
      { id: 'dh4', text: 'Can the vendor return or delete your data upon contract termination?', category: 'Data Lifecycle', weight: 9 },
      { id: 'dh5', text: 'Does the vendor comply with GDPR, CCPA, or relevant privacy regulations?', category: 'Compliance', weight: 9 },
      { id: 'dh6', text: 'Does the vendor have a privacy policy that addresses your data?', category: 'Policy', weight: 8 },
      { id: 'dh7', text: 'Does the vendor limit access to your data on a need-to-know basis?', category: 'Access Control', weight: 8 },
      { id: 'dh8', text: 'Does the vendor prohibit using your data for their own purposes?', category: 'Data Usage', weight: 9 },
    ],
  },
  {
    id: 'incident-management',
    title: 'Incident Management',
    description: 'Vendor&apos;s ability to detect, respond to, and recover from incidents',
    questions: [
      { id: 'im1', text: 'Does the vendor have a documented incident response plan?', category: 'Planning', weight: 10 },
      { id: 'im2', text: 'Will the vendor notify you of security incidents within 24-72 hours?', category: 'Notification', weight: 10 },
      { id: 'im3', text: 'Does the vendor have 24/7 security monitoring capabilities?', category: 'Detection', weight: 9 },
      { id: 'im4', text: 'Has the vendor experienced any significant security breaches in the past 3 years?', category: 'History', weight: 8 },
      { id: 'im5', text: 'Does the vendor have cyber insurance coverage?', category: 'Insurance', weight: 7 },
      { id: 'im6', text: 'Does the vendor conduct regular incident response exercises?', category: 'Testing', weight: 8 },
    ],
  },
  {
    id: 'business-continuity',
    title: 'Business Continuity',
    description: 'Vendor&apos;s resilience and disaster recovery capabilities',
    questions: [
      { id: 'bc1', text: 'Does the vendor have a documented business continuity plan?', category: 'Planning', weight: 9 },
      { id: 'bc2', text: 'Does the vendor have a disaster recovery plan with defined RTOs/RPOs?', category: 'DR', weight: 9 },
      { id: 'bc3', text: 'Does the vendor have redundant infrastructure/failover capabilities?', category: 'Infrastructure', weight: 8 },
      { id: 'bc4', text: 'Does the vendor regularly test BC/DR plans?', category: 'Testing', weight: 8 },
      { id: 'bc5', text: 'Does the vendor have documented SLAs for uptime and availability?', category: 'SLA', weight: 8 },
      { id: 'bc6', text: 'What is the vendor&apos;s historical uptime over the past 12 months?', category: 'Performance', weight: 7 },
    ],
  },
  {
    id: 'access-management',
    title: 'Access & Identity Management',
    description: 'How the vendor controls access to systems and data',
    questions: [
      { id: 'am1', text: 'Does the vendor use role-based access control (RBAC)?', category: 'Access Control', weight: 9 },
      { id: 'am2', text: 'Does the vendor have a privileged access management (PAM) solution?', category: 'PAM', weight: 8 },
      { id: 'am3', text: 'Are access reviews conducted regularly (at least quarterly)?', category: 'Reviews', weight: 8 },
      { id: 'am4', text: 'Are user accounts promptly deprovisioned when employees leave?', category: 'Offboarding', weight: 9 },
      { id: 'am5', text: 'Does the vendor log and monitor administrative access?', category: 'Monitoring', weight: 8 },
      { id: 'am6', text: 'Does the vendor support SSO/federation for your users?', category: 'Integration', weight: 7 },
    ],
  },
  {
    id: 'subcontractors',
    title: 'Subcontractors & Fourth Parties',
    description: 'Vendor&apos;s management of their own suppliers',
    questions: [
      { id: 'sub1', text: 'Does the vendor disclose their critical subcontractors/fourth parties?', category: 'Transparency', weight: 9 },
      { id: 'sub2', text: 'Does the vendor assess security of their subcontractors?', category: 'Assessment', weight: 9 },
      { id: 'sub3', text: 'Does the vendor require subcontractors to meet security standards?', category: 'Requirements', weight: 8 },
      { id: 'sub4', text: 'Will the vendor notify you before changing critical subcontractors?', category: 'Notification', weight: 8 },
      { id: 'sub5', text: 'Are subcontractors contractually bound to the same security requirements?', category: 'Contracts', weight: 8 },
    ],
  },
  {
    id: 'governance',
    title: 'Governance & Compliance',
    description: 'Organizational governance and regulatory compliance',
    questions: [
      { id: 'gv1', text: 'Does the vendor have a dedicated security team or function?', category: 'Organization', weight: 9 },
      { id: 'gv2', text: 'Does the vendor conduct security awareness training for employees?', category: 'Training', weight: 8 },
      { id: 'gv3', text: 'Does the vendor perform background checks on employees?', category: 'HR Security', weight: 8 },
      { id: 'gv4', text: 'Does the vendor have documented security policies and procedures?', category: 'Policies', weight: 9 },
      { id: 'gv5', text: 'Will the vendor allow your security audits or assessments?', category: 'Audit Rights', weight: 8 },
      { id: 'gv6', text: 'Does the vendor have appropriate regulatory certifications for your industry?', category: 'Compliance', weight: 8 },
    ],
  },
  {
    id: 'financial',
    title: 'Financial & Operational Stability',
    description: 'Vendor viability and operational health',
    questions: [
      { id: 'fn1', text: 'Is the vendor financially stable (positive indicators, funding, revenue)?', category: 'Financial Health', weight: 8 },
      { id: 'fn2', text: 'How long has the vendor been in business?', category: 'Maturity', weight: 6 },
      { id: 'fn3', text: 'Does the vendor have references from similar-sized companies?', category: 'References', weight: 7 },
      { id: 'fn4', text: 'Does the vendor have appropriate insurance coverage (E&O, cyber liability)?', category: 'Insurance', weight: 7 },
      { id: 'fn5', text: 'Are there any pending lawsuits or regulatory actions against the vendor?', category: 'Legal', weight: 8 },
    ],
  },
];

const criticality = [
  { value: 'critical', label: 'Critical - Core business functions, high data access', multiplier: 1.5 },
  { value: 'high', label: 'High - Important functions, moderate data access', multiplier: 1.2 },
  { value: 'medium', label: 'Medium - Supporting functions, limited data access', multiplier: 1.0 },
  { value: 'low', label: 'Low - Minimal business impact, no sensitive data', multiplier: 0.8 },
];

export default function VendorRiskAssessment() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na' | 'unknown'>>({});
  const [vendorName, setVendorName] = useState('');
  const [vendorCriticality, setVendorCriticality] = useState('');
  const [dataTypes, setDataTypes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'vendor-risk-assessment',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na' | 'unknown') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  useEffect(() => {
    if (view === 'results') {
      saveToolScore('vendor-risk-assessment', calculateScore().score);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleDataTypeToggle = (type: string) => {
    setDataTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { category: string; question: string; status: string; risk: string }[] = [];

    const criticalityObj = criticality.find((c) => c.value === vendorCriticality);
    const multiplier = criticalityObj?.multiplier || 1;

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({
            category: q.category,
            question: q.text,
            status: 'Partial',
            risk: q.weight >= 9 ? 'High' : q.weight >= 7 ? 'Medium' : 'Low',
          });
        } else if (answer === 'no' || answer === 'unknown') {
          gaps.push({
            category: q.category,
            question: q.text,
            status: answer === 'unknown' ? 'Unknown' : 'No',
            risk: q.weight >= 9 ? 'High' : q.weight >= 7 ? 'Medium' : 'Low',
          });
        }
      });
    });

    const rawScore = possible > 0 ? (earned / possible) * 100 : 0;
    const adjustedScore = Math.min(100, Math.max(0, rawScore / multiplier));

    return {
      score: Math.round(adjustedScore),
      rawScore: Math.round(rawScore),
      gaps: gaps.sort((a, b) => {
        const riskOrder = { High: 0, Medium: 1, Low: 2 };
        return riskOrder[a.risk as keyof typeof riskOrder] - riskOrder[b.risk as keyof typeof riskOrder];
      }),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getRiskLevel = (score: number) => {
    if (score >= 85) return { level: 'Low Risk', color: 'text-green-400', bg: 'bg-green-500/20', recommendation: 'Proceed' };
    if (score >= 70) return { level: 'Moderate Risk', color: 'text-yellow-400', bg: 'bg-yellow-500/20', recommendation: 'Conditional Approval' };
    if (score >= 50) return { level: 'High Risk', color: 'text-accent-400', bg: 'bg-accent-500/20', recommendation: 'Additional Controls Required' };
    return { level: 'Critical Risk', color: 'text-red-400', bg: 'bg-red-500/20', recommendation: 'Not Recommended' };
  };

  const exportToMarkdown = () => {
    const { score, rawScore, gaps } = calculateScore();
    const risk = getRiskLevel(score);

    let md = `# Vendor Risk Assessment Report\n\n`;
    md += `**Vendor:** ${vendorName || 'Not specified'}\n`;
    md += `**Assessment Date:** ${new Date().toLocaleDateString()}\n`;
    md += `**Criticality:** ${criticality.find((c) => c.value === vendorCriticality)?.label || 'Not specified'}\n\n`;

    if (dataTypes.length > 0) {
      md += `**Data Types Accessed:** ${dataTypes.join(', ')}\n\n`;
    }

    md += `## Risk Summary\n\n`;
    md += `| Metric | Value |\n`;
    md += `|--------|-------|\n`;
    md += `| Overall Risk Score | ${score}% |\n`;
    md += `| Raw Score | ${rawScore}% |\n`;
    md += `| Risk Level | ${risk.level} |\n`;
    md += `| Recommendation | ${risk.recommendation} |\n\n`;

    md += `## Section Scores\n\n`;
    md += `| Section | Score |\n`;
    md += `|---------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Risk Findings\n\n`;
      md += `### High Risk Items\n\n`;
      const highRisk = gaps.filter((g) => g.risk === 'High');
      if (highRisk.length > 0) {
        highRisk.forEach((gap) => {
          md += `- **${gap.category}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No high-risk items identified.\n`;
      }

      md += `\n### Medium Risk Items\n\n`;
      const mediumRisk = gaps.filter((g) => g.risk === 'Medium');
      if (mediumRisk.length > 0) {
        mediumRisk.forEach((gap) => {
          md += `- **${gap.category}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No medium-risk items identified.\n`;
      }

      md += `\n### Low Risk Items\n\n`;
      const lowRisk = gaps.filter((g) => g.risk === 'Low');
      if (lowRisk.length > 0) {
        lowRisk.forEach((gap) => {
          md += `- **${gap.category}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No low-risk items identified.\n`;
      }
    }

    md += `\n## Recommended Actions\n\n`;
    if (score < 70) {
      md += `1. Request vendor security questionnaire completion\n`;
      md += `2. Require evidence of certifications (SOC 2, ISO 27001)\n`;
      md += `3. Review vendor&apos;s incident response capabilities\n`;
      md += `4. Negotiate specific security clauses in contract\n`;
      md += `5. Implement additional monitoring controls\n`;
      md += `6. Consider alternative vendors\n`;
    } else if (score < 85) {
      md += `1. Address high-priority gaps before engagement\n`;
      md += `2. Include specific security requirements in contract\n`;
      md += `3. Schedule regular security review meetings\n`;
      md += `4. Implement monitoring for critical integrations\n`;
    } else {
      md += `1. Proceed with standard vendor onboarding\n`;
      md += `2. Conduct annual reassessment\n`;
      md += `3. Monitor vendor security posture continuously\n`;
    }

    md += `\n## Contractual Recommendations\n\n`;
    md += `- Security requirements aligned with your standards\n`;
    md += `- Incident notification within 24-72 hours\n`;
    md += `- Right to audit or receive attestation reports\n`;
    md += `- Data handling and disposal requirements\n`;
    md += `- Subcontractor notification requirements\n`;
    md += `- Cyber insurance minimums\n`;

    md += `\n---\n*Assessment conducted using The Art of CTO Vendor Risk Assessment*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-risk-${vendorName.replace(/\s+/g, '-').toLowerCase() || 'assessment'}-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    const { score, rawScore, gaps } = calculateScore();
    const risk = getRiskLevel(score);
    if (!commandCenterEnabled) return;

    const formattedGaps = gaps.map((gap, index) => ({
      id: `gap-${index}`,
      title: gap.question,
      severity: gap.risk === 'High' ? 'critical' as const :
                gap.risk === 'Medium' ? 'high' as const : 'medium' as const,
      description: `Category: ${gap.category} - Status: ${gap.status}`,
      recommendation: `Address this ${gap.risk.toLowerCase()} risk item to improve vendor security posture`,
    }));

    const recommendations = sections
      .map(section => ({
        id: section.id,
        title: `Improve ${section.title}`,
        priority: getSectionScore(section.id) < 50 ? 'high' as const :
                  getSectionScore(section.id) < 70 ? 'medium' as const : 'low' as const,
        description: `Current score: ${getSectionScore(section.id)}%. Focus on addressing gaps in ${section.title.toLowerCase()}.`,
        effort: section.questions.filter(q => answers[q.id] !== 'yes').length > 5 ? 'High' :
                section.questions.filter(q => answers[q.id] !== 'yes').length > 2 ? 'Medium' : 'Low',
      }))
      .filter(r => r.priority !== 'low');

    const scores: Record<string, number> = {};
    sections.forEach(s => {
      scores[s.id] = getSectionScore(s.id);
    });

    const assessment = await saveResult({
      name: vendorName ? `Vendor Risk: ${vendorName} - ${new Date().toLocaleDateString()}` : `Vendor Risk Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: score,
      scores,
      answers,
      gaps: formattedGaps,
      recommendations,
      metadata: {
        vendorName: vendorName || 'Not specified',
        vendorCriticality: criticality.find(c => c.value === vendorCriticality)?.label || 'Not specified',
        dataTypes,
        rawScore,
        riskLevel: risk.level,
        recommendation: risk.recommendation,
        totalGaps: gaps.length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              Third-Party Risk
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              Vendor Risk Assessment
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Evaluate the security posture of vendors and third parties before engagement.
              Identify risks across security controls, data handling, and business continuity.
            </p>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Vendor Information</h3>

            <div className="mb-4">
              <label className="text-foreground-secondary text-sm block mb-2">Vendor Name</label>
              <input
                type="text"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="Enter vendor name..."
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground placeholder-foreground-muted"
              />
            </div>

            <div className="mb-4">
              <label className="text-foreground-secondary text-sm block mb-2">Vendor Criticality</label>
              <select
                value={vendorCriticality}
                onChange={(e) => setVendorCriticality(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select criticality level...</option>
                {criticality.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-foreground-secondary text-sm block mb-2">
                Data Types the Vendor Will Access (select all that apply)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  'PII (Personal Information)',
                  'PHI (Health Information)',
                  'Financial Data',
                  'Payment Card Data',
                  'Intellectual Property',
                  'Employee Data',
                  'Customer Data',
                  'System Credentials',
                ].map((type) => (
                  <button
                    key={type}
                    onClick={() => handleDataTypeToggle(type)}
                    className={`px-3 py-2 rounded text-sm text-left transition-colors ${
                      dataTypes.includes(type)
                        ? 'bg-accent-muted text-accent border border-border-muted0'
                        : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Assessment Categories</h3>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-foreground-secondary">
              {sections.map((section) => (
                <div key={section.id} className="flex gap-2">
                  <span className="text-accent">•</span>
                  <span>{section.title}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Risk-Based Approach</h3>
            <p className="text-foreground-secondary text-sm">
              This assessment adjusts scores based on vendor criticality. Critical vendors
              require higher security standards than low-criticality vendors. Gaps are
              prioritized by risk level to focus remediation efforts.
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 20-25 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.category}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(['yes', 'partial', 'no', 'unknown', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : value === 'unknown'
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, rawScore, gaps } = calculateScore();
  const risk = getRiskLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Vendor Risk Assessment Results
          </h1>
          {vendorName && (
            <p className="text-foreground-tertiary text-lg mb-4">{vendorName}</p>
          )}
          <div className={`inline-block px-6 py-3 rounded-lg ${risk.bg}`}>
            <span className={`text-4xl font-bold ${risk.color}`}>{score}%</span>
            <p className={`${risk.color} font-medium mt-1`}>{risk.level}</p>
          </div>
          <p className={`mt-3 text-lg font-medium ${risk.color}`}>
            Recommendation: {risk.recommendation}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-surface border border-border-muted rounded-lg p-4">
            <h3 className="text-foreground-tertiary text-sm mb-1">Criticality</h3>
            <p className="text-foreground font-semibold">
              {criticality.find((c) => c.value === vendorCriticality)?.label.split(' - ')[0] || 'Not specified'}
            </p>
          </div>
          <div className="bg-surface border border-border-muted rounded-lg p-4">
            <h3 className="text-foreground-tertiary text-sm mb-1">Raw Score</h3>
            <p className="text-foreground font-semibold">{rawScore}%</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Section Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionRisk = getRiskLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionRisk.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 85
                          ? 'bg-green-500'
                          : sectionScore >= 70
                          ? 'bg-yellow-500'
                          : sectionScore >= 50
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Risk Findings ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.category}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <div className="flex gap-2">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                          gap.risk === 'High'
                            ? 'bg-red-500/20 text-red-400'
                            : gap.risk === 'Medium'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}
                      >
                        {gap.risk}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Key Contractual Terms</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-accent">•</span> Security requirements aligned with your policies</p>
            <p><span className="text-accent">•</span> Incident notification within 24-72 hours</p>
            <p><span className="text-accent">•</span> Right to audit or receive attestation reports annually</p>
            <p><span className="text-accent">•</span> Data handling, retention, and disposal requirements</p>
            <p><span className="text-accent">•</span> Subcontractor notification and approval requirements</p>
            <p><span className="text-accent">•</span> Cyber liability insurance minimums</p>
            <p><span className="text-accent">•</span> Termination and data return provisions</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <button
            onClick={exportToMarkdown}
            className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
          >
            Export Report
          </button>
          {commandCenterEnabled && !saved && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save to Command Center'}
            </button>
          )}
          {saved && (
            <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved to Command Center
            </div>
          )}
          {saveError && (
            <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
              {saveError}
            </div>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
